import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
  Upload, Trash2, FileText, FileImage, FileCheck, AlertCircle,
  Plus, X, CheckCircle, Clock, Download, Shield, ClipboardList, ExternalLink,
} from 'lucide-react';
import { clientDocuments, clientDocumentRequests, forms as formsApi } from '../utils/api';

/**
 * ClientDocumentsPanel
 * ──────────────────────
 * Unified panel for listing, uploading, requesting, and managing
 * documents attached to a single client (a `users` row with
 * role='client'). Used by:
 *
 *   - The client's own Profile page (viewerRole='client')
 *   - Admin/groomer ClientManagement page (viewerRole='admin'|'groomer')
 *
 * Behaviour adapts to the viewer's role:
 *   - Admins see admin-only controls: "Request a document", approve
 *     pending uploads, delete any document.
 *   - Groomers see what the admin has enabled via system settings.
 *   - Clients see their pending requests as "Action needed" prompts
 *     with per-request upload buttons. Free-form upload appears only
 *     when the matching toggles are on. Delete is allowed on their
 *     own documents.
 *
 * The panel is self-contained: it loads settings, documents, and
 * requests on mount and re-fetches after each mutation.
 */

const TYPE_LABELS = {
  vaccine_record: 'Vaccine record',
  waiver: 'Waiver',
  contract: 'Contract',
  form_of_id: 'Form of ID',
  health_records: 'Health records',
  other: 'Other',
};

const ALL_TYPES = ['vaccine_record', 'waiver', 'contract', 'form_of_id', 'health_records', 'other'];

const formatBytes = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const fileIconFor = (mime) => {
  if (mime?.startsWith('image/')) return FileImage;
  if (mime === 'application/pdf') return FileText;
  return FileCheck;
};

const ClientDocumentsPanel = ({ clientId, viewerRole = 'client', clientName = '' }) => {
  const [settings, setSettings] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingFor, setUploadingFor] = useState(null); // request id, or 'free' for unrequested
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  // When non-null, the request modal is in "edit" mode for this id.
  const [editingRequestId, setEditingRequestId] = useState(null);
  const fileInputRef = useRef(null);
  const requestUploadInputRef = useRef(null);

  // Free-form upload form state
  const [uploadForm, setUploadForm] = useState({ documentType: 'other', label: '', file: null });

  // Admin "Request a document" form state. `kind` switches between a
  // file upload (default) and a structured-form fill. When kind='form',
  // form_id is required and documentType is irrelevant (the form itself
  // defines what the client is being asked).
  const [requestForm, setRequestForm] = useState({
    title: '', instructions: '', documentType: 'other', required: false,
    kind: 'document', formId: '',
  });

  // Available forms for the kind='form' picker. Loaded lazily the first
  // time the request modal is opened by an admin so we don't hit
  // /api/forms on every panel mount.
  const [availableForms, setAvailableForms] = useState(null);
  const [loadingForms, setLoadingForms] = useState(false);

  const isAdmin = viewerRole === 'admin';
  const isGroomer = viewerRole === 'groomer';
  const isClient = viewerRole === 'client';

  const loadAll = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    // Use allSettled so a failure in one endpoint (e.g. document-requests
    // 500ing because migration 038 hasn't been applied yet, or a 403 for
    // a groomer who doesn't have the manage flag) doesn't blank the
    // whole panel. We still surface real errors via toast but degrade
    // gracefully on partial failures.
    const [settingsRes, docsRes, reqsRes] = await Promise.allSettled([
      clientDocuments.getSettings(),
      clientDocuments.list(clientId),
      clientDocumentRequests.list(clientId),
    ]);

    if (settingsRes.status === 'fulfilled') {
      setSettings(settingsRes.value.data || {});
    } else {
      setSettings({});
      // Settings is the most important call — if it fails everything is
      // degraded. Surface that to the user.
      if (settingsRes.reason?.response?.status !== 403) {
        toast.error('Failed to load document settings');
      }
    }

    if (docsRes.status === 'fulfilled') {
      setDocuments(docsRes.value.data || []);
    } else {
      setDocuments([]);
      if (docsRes.reason?.response?.status !== 403) {
        toast.error('Failed to load documents');
      }
    }

    if (reqsRes.status === 'fulfilled') {
      setRequests(reqsRes.value.data || []);
    } else {
      setRequests([]);
      // Don't toast on 403 (expected for some roles) or 500 with a
      // table-missing error — the user already sees the docs section
      // working and a blank requests list is acceptable.
      if (reqsRes.reason?.response?.status === 401) {
        toast.error('Failed to load document requests');
      }
    }

    setLoading(false);
  }, [clientId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Derived: which document types may a CLIENT pick for free-form uploads?
  const clientAllowedTypes = useMemo(() => {
    const list = settings?.client_upload_allowed_types;
    if (Array.isArray(list) && list.length > 0) return list;
    return ALL_TYPES;
  }, [settings]);

  // For admins/groomers, all types are pickable. For clients, only the
  // configured allowlist.
  const pickableTypes = isClient ? clientAllowedTypes : ALL_TYPES;

  const canShowFreeFormUpload = useMemo(() => {
    if (!settings) return false;
    if (isAdmin) return true;
    if (isGroomer) return !!settings.employees_can_manage_client_documents;
    if (isClient) {
      return !!settings.clients_can_upload_documents
        && !!settings.clients_can_upload_unrequested_documents
        && pickableTypes.length > 0;
    }
    return false;
  }, [settings, isAdmin, isGroomer, isClient, pickableTypes]);

  const canCreateRequest = isAdmin;
  const canApprove = isAdmin;
  const canDelete = (doc) => {
    if (isAdmin) return true;
    if (isGroomer) return !!settings?.employees_can_manage_client_documents
      && doc.uploaded_by && doc.uploaded_by === doc.viewer_id; // viewer_id not provided; fall back to never
    if (isClient) return true; // backend enforces "own profile only"
    return false;
  };

  // ── Upload handlers ────────────────────────────────────────────────

  const handleFreeFormUpload = async () => {
    if (!uploadForm.file) {
      toast.error('Please choose a file to upload');
      return;
    }
    try {
      await clientDocuments.upload(clientId, uploadForm.file, {
        documentType: uploadForm.documentType,
        label: uploadForm.label || undefined,
      });
      toast.success(
        isClient && settings?.client_uploads_require_approval
          ? 'Uploaded — your provider will review it shortly'
          : 'Document uploaded'
      );
      setShowUploadModal(false);
      setUploadForm({ documentType: 'other', label: '', file: null });
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    }
  };

  const handleRequestUpload = async (request, file) => {
    setUploadingFor(request.id);
    try {
      await clientDocuments.upload(clientId, file, {
        documentType: request.document_type,
        label: request.title,
        requestId: request.id,
      });
      toast.success('Document uploaded');
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploadingFor(null);
    }
  };

  // ── Document mutations ─────────────────────────────────────────────

  const handleDelete = async (doc) => {
    const fname = doc.label || doc.filename || 'this document';
    if (!window.confirm(`Delete "${fname}"? This cannot be undone.`)) return;
    try {
      await clientDocuments.delete(clientId, doc.id);
      toast.success('Document deleted');
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  };

  const handleApprove = async (doc) => {
    try {
      await clientDocuments.approve(clientId, doc.id);
      toast.success('Document approved');
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Approve failed');
    }
  };

  // ── Request mutations ──────────────────────────────────────────────

  // Lazy-load the active-forms list for the kind='form' picker. Called
  // when the request modal opens. Inactive forms are filtered out
  // client-side because the backend won't accept them anyway and we
  // don't want to surface dead options to the admin.
  const ensureFormsLoaded = useCallback(async () => {
    if (availableForms !== null || loadingForms) return;
    setLoadingForms(true);
    try {
      const res = await formsApi.list();
      setAvailableForms((res.data || []).filter((f) => !!f.is_active));
    } catch (err) {
      setAvailableForms([]);
      // Silent — the kind='form' option will just show an empty picker
      // with a hint to create a form first.
    } finally {
      setLoadingForms(false);
    }
  }, [availableForms, loadingForms]);

  // Open the modal in create mode, with a fresh form.
  const openCreateRequest = () => {
    setEditingRequestId(null);
    setRequestForm({ title: '', instructions: '', documentType: 'other', required: false, kind: 'document', formId: '' });
    setShowRequestModal(true);
    ensureFormsLoaded();
  };

  // Open the modal in edit mode, pre-filled from an existing pending request.
  // kind/form_id are read-only post-create (backend enforces) so we still
  // load forms list for the friendly display only.
  const openEditRequest = (req) => {
    setEditingRequestId(req.id);
    setRequestForm({
      title: req.title || '',
      instructions: req.instructions || '',
      documentType: req.document_type || 'other',
      required: !!req.required,
      kind: req.kind || 'document',
      formId: req.form_id || '',
    });
    setShowRequestModal(true);
    if ((req.kind || 'document') === 'form') ensureFormsLoaded();
  };

  const closeRequestModal = () => {
    setShowRequestModal(false);
    setEditingRequestId(null);
    setRequestForm({ title: '', instructions: '', documentType: 'other', required: false, kind: 'document', formId: '' });
  };

  const handleSaveRequest = async () => {
    if (!requestForm.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (requestForm.kind === 'form' && !requestForm.formId) {
      toast.error('Please pick a form');
      return;
    }
    // Build the payload. On edit, kind/formId are frozen server-side so
    // we deliberately omit them to avoid sending no-op fields.
    const payload = editingRequestId
      ? {
          title: requestForm.title,
          instructions: requestForm.instructions,
          documentType: requestForm.documentType,
          required: requestForm.required,
        }
      : {
          title: requestForm.title,
          instructions: requestForm.instructions,
          documentType: requestForm.documentType,
          required: requestForm.required,
          kind: requestForm.kind,
          form_id: requestForm.kind === 'form' ? requestForm.formId : null,
        };
    try {
      if (editingRequestId) {
        await clientDocumentRequests.update(clientId, editingRequestId, payload);
        toast.success('Request updated');
      } else {
        await clientDocumentRequests.create(clientId, payload);
        toast.success('Request created');
      }
      closeRequestModal();
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save request');
    }
  };

  const handleWaiveRequest = async (req) => {
    if (!window.confirm(`Mark "${req.title}" as no longer needed?`)) return;
    try {
      await clientDocumentRequests.waive(clientId, req.id);
      toast.success('Request waived');
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Waive failed');
    }
  };

  const handleDeleteRequest = async (req) => {
    if (!window.confirm(`Delete the request "${req.title}"?`)) return;
    try {
      await clientDocumentRequests.delete(clientId, req.id);
      toast.success('Request deleted');
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  };

  // ── Renders ────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="card flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  const pendingRequests = requests.filter((r) => r.status === 'pending');
  const otherRequests   = requests.filter((r) => r.status !== 'pending');

  const heading = isClient
    ? 'My Documents & Forms'
    : (clientName ? `Documents & Forms — ${clientName}` : 'Documents & Forms');

  return (
    <div className="space-y-6">
      {/* ── Pending requests (Action needed) ─────────────────────── */}
      {pendingRequests.length > 0 && (
        <div className="card border-amber-200 bg-amber-50">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="text-amber-600" size={20} />
            <h3 className="text-lg font-bold text-amber-900">
              {isClient ? 'Action needed' : 'Pending requests'}
            </h3>
            <span className="ml-auto bg-amber-200 text-amber-800 text-xs font-medium px-2 py-0.5 rounded-full">
              {pendingRequests.length}
            </span>
          </div>
          <div className="space-y-3">
            {pendingRequests.map((req) => (
              <div key={req.id} className="bg-white rounded-lg border border-amber-200 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-gray-900">{req.title}</h4>
                      {req.kind === 'form' ? (
                        <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-800 rounded inline-flex items-center gap-1">
                          <ClipboardList size={12} /> Form{req.form_name ? `: ${req.form_name}` : ''}
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded">
                          {TYPE_LABELS[req.document_type] || req.document_type}
                        </span>
                      )}
                      {req.required ? (
                        <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded">Required</span>
                      ) : null}
                    </div>
                    {req.instructions && (
                      <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{req.instructions}</p>
                    )}
                    {!isClient && (
                      <p className="text-xs text-gray-500 mt-2">
                        Created {new Date(req.created_at).toLocaleDateString()}
                        {req.creator_first_name ? ` by ${req.creator_first_name} ${req.creator_last_name || ''}` : ''}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    {/* Per-request CTA: file upload for kind='document',
                        "Fill out form" link for kind='form'. The form
                        share-token is pre-spawned at request-creation
                        time so the link always works for the same
                        request, and the form-submit handler will flip
                        the request to fulfilled. */}
                    {req.kind === 'form' ? (
                      req.form_share_token ? (
                        <a
                          href={`/forms/fill/${req.form_share_token}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-primary flex items-center gap-2"
                          onClick={() => {
                            // Refresh on focus so the panel picks up the
                            // fulfillment as soon as the user returns.
                            const refresh = () => {
                              window.removeEventListener('focus', refresh);
                              loadAll();
                            };
                            window.addEventListener('focus', refresh);
                          }}
                        >
                          <ClipboardList size={16} />
                          <span>Fill out form</span>
                          <ExternalLink size={12} />
                        </a>
                      ) : (
                        <span className="text-xs text-amber-700 italic">
                          Share link missing — ask admin to recreate
                        </span>
                      )
                    ) : (
                      <label className="btn btn-primary cursor-pointer flex items-center gap-2">
                        <Upload size={16} />
                        <span>{uploadingFor === req.id ? 'Uploading…' : 'Upload'}</span>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*,application/pdf"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) handleRequestUpload(req, f);
                            e.target.value = '';
                          }}
                          disabled={uploadingFor === req.id}
                        />
                      </label>
                    )}
                    {/* Admin: extra controls */}
                    {isAdmin && (
                      <div className="flex gap-1">
                        <button
                          onClick={() => openEditRequest(req)}
                          className="text-xs text-primary-600 hover:text-primary-800 underline"
                          title="Edit title, instructions, type, or required flag"
                        >
                          Edit
                        </button>
                        <span className="text-gray-300">|</span>
                        <button
                          onClick={() => handleWaiveRequest(req)}
                          className="text-xs text-gray-600 hover:text-gray-900 underline"
                          title="Mark as no longer needed"
                        >
                          Waive
                        </button>
                        <span className="text-gray-300">|</span>
                        <button
                          onClick={() => handleDeleteRequest(req)}
                          className="text-xs text-red-600 hover:text-red-800 underline"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Header: Documents list + actions ─────────────────────── */}
      <div className="card">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="text-lg font-bold text-gray-900">{heading}</h3>
          <div className="flex gap-2 flex-wrap">
            {canCreateRequest && (
              <button
                onClick={openCreateRequest}
                className="btn btn-secondary flex items-center gap-2"
              >
                <Plus size={16} />
                <span>Request something</span>
              </button>
            )}
            {canShowFreeFormUpload && (
              <button
                onClick={() => setShowUploadModal(true)}
                className="btn btn-primary flex items-center gap-2"
              >
                <Upload size={16} />
                <span>Upload</span>
              </button>
            )}
          </div>
        </div>

        {/* Empty / list */}
        {documents.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FileText className="mx-auto mb-2 text-gray-400" size={32} />
            <p className="text-sm">
              {isClient
                ? 'No documents on file yet.'
                : `No documents have been uploaded${clientName ? ` for ${clientName}` : ''}.`}
            </p>
            {!canShowFreeFormUpload && isClient && (
              <p className="text-xs mt-2 text-gray-400">
                Document uploads aren't currently enabled. Your provider will let you know if they need anything.
              </p>
            )}
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {documents.map((doc) => {
              const Icon = fileIconFor(doc.mime_type);
              const pending = doc.status === 'pending';
              return (
                <li key={doc.id} className="py-3 flex items-start gap-3">
                  <div className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${pending ? 'bg-amber-100 text-amber-700' : 'bg-primary-50 text-primary-700'}`}>
                    <Icon size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900 truncate">
                        {doc.label || doc.filename}
                      </span>
                      <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded">
                        {TYPE_LABELS[doc.document_type] || doc.document_type}
                      </span>
                      {pending && (
                        <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-800 rounded inline-flex items-center gap-1">
                          <Clock size={12} /> Pending review
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {doc.filename}
                      {doc.byte_size ? ` · ${formatBytes(doc.byte_size)}` : ''}
                      {' · '}{new Date(doc.uploaded_at).toLocaleDateString()}
                      {doc.uploader_first_name && (
                        <> · uploaded by {doc.uploader_first_name} {doc.uploader_last_name || ''}{' '}
                          {doc.uploader_role && doc.uploader_role !== 'client' && (
                            <span className="inline-flex items-center gap-1 text-gray-600">
                              <Shield size={10} /> {doc.uploader_role}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <a
                      href={doc.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-secondary p-2"
                      title="View / download"
                    >
                      <Download size={16} />
                    </a>
                    {canApprove && pending && (
                      <button
                        onClick={() => handleApprove(doc)}
                        className="btn btn-primary p-2"
                        title="Approve"
                      >
                        <CheckCircle size={16} />
                      </button>
                    )}
                    {canDelete(doc) && (
                      <button
                        onClick={() => handleDelete(doc)}
                        className="text-red-600 hover:text-red-800 p-2"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* ── Resolved requests (history) — admin/staff only ───────── */}
      {!isClient && otherRequests.length > 0 && (
        <details className="card">
          <summary className="cursor-pointer font-medium text-gray-700 select-none">
            Resolved requests ({otherRequests.length})
          </summary>
          <ul className="divide-y divide-gray-100 mt-3">
            {otherRequests.map((req) => (
              <li key={req.id} className="py-2 flex items-center gap-3">
                <span className={`text-xs px-2 py-0.5 rounded ${req.status === 'fulfilled' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
                  {req.status}
                </span>
                <span className="font-medium text-gray-900">{req.title}</span>
                <span className="text-xs text-gray-500 ml-auto">
                  {req.resolved_at ? new Date(req.resolved_at).toLocaleDateString() : ''}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {/* ── Free-form Upload Modal ───────────────────────────────── */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Upload a document</h3>
              <button onClick={() => setShowUploadModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Document type</label>
                <select
                  value={uploadForm.documentType}
                  onChange={(e) => setUploadForm({ ...uploadForm, documentType: e.target.value })}
                  className="input w-full"
                >
                  {pickableTypes.map((t) => (
                    <option key={t} value={t}>{TYPE_LABELS[t] || t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Label <span className="text-gray-400 text-xs">(optional)</span></label>
                <input
                  type="text"
                  value={uploadForm.label}
                  onChange={(e) => setUploadForm({ ...uploadForm, label: e.target.value })}
                  className="input w-full"
                  placeholder='e.g. "Rabies certificate 2026"'
                  maxLength={200}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">File</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files?.[0] || null })}
                  className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-4 file:rounded file:border-0 file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                />
                <p className="text-xs text-gray-500 mt-1">Image (JPG/PNG/HEIC) or PDF, up to 10 MB.</p>
              </div>
              {isClient && settings?.client_uploads_require_approval && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  Your upload will be marked as <strong>pending</strong> until your provider approves it.
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowUploadModal(false)} className="btn btn-secondary">Cancel</button>
              <button onClick={handleFreeFormUpload} className="btn btn-primary">Upload</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Create / Edit Request Modal (admin only) ────────────────── */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">
                {editingRequestId
                  ? 'Edit request'
                  : `Request from ${clientName || 'this client'}`}
              </h3>
              <button onClick={closeRequestModal} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              {/* Kind picker (create only). Once a request is created
                  with kind='form', its share-link is already pinned to a
                  specific form so kind/formId are server-frozen. */}
              {!editingRequestId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">What do you need?</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setRequestForm({ ...requestForm, kind: 'document' })}
                      className={`p-3 rounded-lg border text-left transition ${requestForm.kind === 'document' ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:bg-gray-50'}`}
                    >
                      <div className="flex items-center gap-2 font-medium text-gray-900">
                        <Upload size={16} /> File upload
                      </div>
                      <p className="text-xs text-gray-600 mt-1">Client uploads a document (PDF, image).</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => { setRequestForm({ ...requestForm, kind: 'form' }); ensureFormsLoaded(); }}
                      className={`p-3 rounded-lg border text-left transition ${requestForm.kind === 'form' ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:bg-gray-50'}`}
                    >
                      <div className="flex items-center gap-2 font-medium text-gray-900">
                        <ClipboardList size={16} /> Fill out form
                      </div>
                      <p className="text-xs text-gray-600 mt-1">Client fills a structured form you've built.</p>
                    </button>
                  </div>
                </div>
              )}

              {requestForm.kind === 'form' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Form <span className="text-red-500">*</span>
                  </label>
                  {editingRequestId ? (
                    <input
                      type="text"
                      value={availableForms?.find((f) => f.id === requestForm.formId)?.name || 'Form (locked once created)'}
                      disabled
                      className="input w-full bg-gray-50 text-gray-600"
                    />
                  ) : loadingForms ? (
                    <div className="text-sm text-gray-500">Loading forms…</div>
                  ) : (availableForms || []).length === 0 ? (
                    <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                      No active forms yet. Create one under <strong>Forms</strong> first, then come back.
                    </div>
                  ) : (
                    <select
                      value={requestForm.formId}
                      onChange={(e) => setRequestForm({ ...requestForm, formId: e.target.value })}
                      className="input w-full"
                    >
                      <option value="">— pick a form —</option>
                      {availableForms.map((f) => (
                        <option key={f.id} value={f.id}>{f.name}</option>
                      ))}
                    </select>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    The form's questions define what's collected. The title and instructions below are just
                    what the client sees in their request list.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={requestForm.title}
                  onChange={(e) => setRequestForm({ ...requestForm, title: e.target.value })}
                  className="input w-full"
                  placeholder={requestForm.kind === 'form' ? 'e.g. "Intake questionnaire"' : 'e.g. "Rabies certificate"'}
                  maxLength={200}
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Instructions <span className="text-gray-400 text-xs">(optional)</span></label>
                <textarea
                  value={requestForm.instructions}
                  onChange={(e) => setRequestForm({ ...requestForm, instructions: e.target.value })}
                  className="input w-full"
                  rows={3}
                  placeholder={requestForm.kind === 'form'
                    ? "Anything the client should know before opening the form."
                    : "Tell the client what you need (e.g. 'Must be current within the last 12 months. PDF preferred.')"}
                />
              </div>
              {requestForm.kind === 'document' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Document type</label>
                  <select
                    value={requestForm.documentType}
                    onChange={(e) => setRequestForm({ ...requestForm, documentType: e.target.value })}
                    className="input w-full"
                  >
                    {ALL_TYPES.map((t) => (
                      <option key={t} value={t}>{TYPE_LABELS[t]}</option>
                    ))}
                  </select>
                </div>
              )}
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={requestForm.required}
                  onChange={(e) => setRequestForm({ ...requestForm, required: e.target.checked })}
                  className="rounded"
                />
                <span>Mark as required</span>
                <span className="text-xs text-gray-500">(used by signup gating + future booking gates)</span>
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={closeRequestModal} className="btn btn-secondary">Cancel</button>
              <button onClick={handleSaveRequest} className="btn btn-primary">
                {editingRequestId ? 'Save changes' : 'Create request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientDocumentsPanel;
